import { c as _c } from "react/compiler-runtime";
import { ActionIcon, Alert, Button, Select, Skeleton, Switch, Chip, Badge, TextInput } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { dbFormSubset, type DbFormSubset } from 'components/hooks/useDbForm';
import { InfoLabel, InfoTip } from 'components/Info';
import { HiRefresh, HiPencil, HiCheckCircle, HiX, HiOutlineCheck, HiClipboard, HiDownload } from 'react-icons/hi';
import { isEmpty, pipe, range } from 'remeda';
import { licenseApi, licensePlans, trialLicenseCfg, zLicense, type License } from 'shared/forecast/license';
import { WorkspaceTypes } from 'shared/forecast/workspaceTypes';
import { twMerge } from 'tailwind-merge';
import { fmtDate } from 'shared/format';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { DateInput } from '@mantine/dates';
import { upperFirst } from '@mantine/hooks';
import { DateTime as D, type DateTime } from 'luxon';
import { RichText } from 'components/form/RichText';
import { useQueryString } from 'utils/useQueryString';
import { useDbForm } from 'components/hooks/useDbForm';
import { env } from 'utils/env';
import { useQuerySbRead, useQuerySbSync } from 'utils/db/useQuerySb';
import { usePortal } from '../portal';
import type { Profile } from '../profileTypes';
import { planColor } from './AdminSubscription';
import { LabelEmptyList } from 'shared/ui';
import { useDebouncedValue } from 'components/hooks';
import { notNull, zx } from 'shared/type';
import { isNullOrEmpty, notNullOrEmpty } from 'shared/collection';
import Link from 'next/link';
import { nav } from '../nav';
import { globalAdminUiStatuses, posthogUrl, zGlobalAdminUI, type GlobalAdminUI } from './subscriptions';
import { objectToCsv } from 'utils/csv';
import { DownloadButton, downloadFileName } from 'components/DownloadButton';
import { CardCollapsable } from 'components/Card';
import { BsStripe } from 'react-icons/bs';
import { TbBrandMonday } from 'react-icons/tb';
import { ActionCard } from 'components/ActionPanel';
const useSubsGlobal = () => {
  const $ = _c(23);
  const {
    query,
    setQuery
  } = useQueryString(zSubsQuery);
  const [searchDebounced] = useDebouncedValue(query.search, 500);
  const portal = usePortal();
  const profile = portal.profiles.global;
  let t0;
  if ($[0] !== query.editing || $[1] !== query.status || $[2] !== searchDebounced) {
    t0 = {
      status: query.status,
      search: searchDebounced,
      editing: query.editing
    };
    $[0] = query.editing;
    $[1] = query.status;
    $[2] = searchDebounced;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== query.editing || $[5] !== query.status || $[6] !== searchDebounced) {
    t1 = builder => {
      let q = builder.select("*");
      const normalFilters = [];
      if (query.status.length > 0) {
        normalFilters.push(`status.in.(${query.status.join(",")})`);
      }
      if (searchDebounced) {
        const searchConditions = [`workspace_cfg->>name.ilike.*${searchDebounced}*`, `owner_name.ilike.*${searchDebounced}*`, `owner_email.ilike.*${searchDebounced}*`];
        normalFilters.push(`or(${searchConditions.join(",")})`);
      }
      const editingFilter = query.editing.length > 0 ? `id.in.(${query.editing.join(",")})` : null;
      if (normalFilters.length > 0 || editingFilter) {
        q = q.or([editingFilter, isNullOrEmpty(normalFilters) ? null : `and(${normalFilters.join(",")})`].filter(notNull).join(","));
      }
      return q;
    };
    $[4] = query.editing;
    $[5] = query.status;
    $[6] = searchDebounced;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== t0 || $[9] !== t1) {
    t2 = {
      table: "workspace_global_admin",
      schema: zGlobalAdminUI,
      dependencies: t0,
      selectFrom: "workspace_global_admin_ui",
      select: t1,
      clientFilter(r) {
        return !!r.workspace_cfg;
      }
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const subsQuery = useQuerySbRead(t2);
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = licenseApi.muteProps("stripeSync");
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== subsQuery) {
    t4 = {
      ...t3,
      onSuccess: () => {
        subsQuery.refetch();
      }
    };
    $[12] = subsQuery;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const stripeSyncMute = useMutation(t4);
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = licenseApi.muteProps("mondaySync");
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const mondaySyncMute = useMutation(t5);
  let t6;
  if ($[15] !== mondaySyncMute || $[16] !== profile || $[17] !== query || $[18] !== searchDebounced || $[19] !== setQuery || $[20] !== stripeSyncMute || $[21] !== subsQuery) {
    t6 = {
      stripeSyncMute,
      mondaySyncMute,
      query,
      setQuery,
      searchDebounced,
      subsQuery,
      profile
    };
    $[15] = mondaySyncMute;
    $[16] = profile;
    $[17] = query;
    $[18] = searchDebounced;
    $[19] = setQuery;
    $[20] = stripeSyncMute;
    $[21] = subsQuery;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
};
type UseSubsGlobal = ReturnType<typeof useSubsGlobal>;
export const AdminSubscriptionsGlobal = () => {
  const $ = _c(9);
  const {
    stripeSyncMute,
    mondaySyncMute,
    query,
    setQuery,
    subsQuery,
    profile
  } = useSubsGlobal();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...licenseApi.muteProps("stripeEventTest")
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const testStripeEvents = useMutation(t0);
  let t1;
  if ($[1] !== mondaySyncMute || $[2] !== profile || $[3] !== query || $[4] !== setQuery || $[5] !== stripeSyncMute || $[6] !== subsQuery || $[7] !== testStripeEvents) {
    t1 = profile && <div className="flex flex-col gap-2 items-start px-4"><h2 className="mt-4">Subscriptions</h2><SubsTable query={query} setQuery={setQuery} subsQuery={subsQuery} profile={profile} /><div className="mt-8 flex gap-4 flex-wrap justify-start">{pipe(stripeSyncMute, m => {
          const buttonProps = {
            variant: "outline",
            leftSection: <HiRefresh />,
            loading: m.isPending,
            disabled: m.isPending
          };
          return <CardCollapsable title={<div className="flex items-center gap-2"><BsStripe /><div>Stripe</div></div>} color="purple" classNames={{
            container: "flex flex-col gap-2 max-w-[30em] p-3"
          }}><InfoLabel>Stripe is synchronized automatically, but in the case of a problem, you can manually sync stripe changes from the last event we received</InfoLabel><Button {...buttonProps} onClick={() => m.mutate({
              syncType: "recent"
            })}>Events → App</Button><Button {...buttonProps} onClick={() => m.mutate({
              syncType: "all"
            })}>Subscriptions → App</Button><Button {...buttonProps} onClick={() => m.mutate({
              syncType: "usage"
            })}>App usage → Meter events</Button>{m.isSuccess && <Alert color="green"><div className="flex flex-col gap-2"><div>Updated {m.data.updatedWorkspaces.length}{" "}{match(m.variables.syncType).with("all", _temp).with("recent", _temp2).with("usage", _temp3).exhaustive()}{m.data.syncedFrom && <> (according to events from {fmtDate(m.data.syncedFrom, {
                      showTz: true
                    })})</>}</div>{notNullOrEmpty(m.data.updatedWorkspaces) && <div className="flex flex-wrap gap-2">Workspaces:{m.data.updatedWorkspaces.map(_temp4)}</div>}</div></Alert>}{m.isError && <Alert color="red" title="Error syncing subscriptions">{m.error.message}</Alert>}<Button className="mt-4" variant="outline" color="var(--mantine-color-primary)" loading={testStripeEvents.isPending} onClick={() => testStripeEvents.mutate({
              profile_id: profile.id,
              workspace_id: profile.workspace_id
            })} leftSection={testStripeEvents.isSuccess ? <HiCheckCircle color="var(--mantine-color-green-5)" /> : <HiClipboard />}>Test app is receiving events</Button>{testStripeEvents.isError && <Alert color="red" title="Error receiving stripe events">{testStripeEvents.error.message}</Alert>}</CardCollapsable>;
        })}<CardCollapsable title={<div className="flex items-center gap-2"><TbBrandMonday /><div>Monday</div></div>} color="teal" classNames={{
          container: "flex flex-col gap-2 max-w-[30em] p-3"
        }}><InfoLabel>Sync trials to the monday leader board</InfoLabel><Button variant="outline" leftSection={<HiRefresh />} disabled={mondaySyncMute.isPending} onClick={() => mondaySyncMute.mutate({
            workspace_id: profile.workspace_id
          })}>App Trials → Leads</Button>{match(mondaySyncMute).with({
            isSuccess: true
          }, _temp5).with({
            isError: true
          }, _temp6).otherwise(_temp7)}</CardCollapsable></div></div>;
    $[1] = mondaySyncMute;
    $[2] = profile;
    $[3] = query;
    $[4] = setQuery;
    $[5] = stripeSyncMute;
    $[6] = subsQuery;
    $[7] = testStripeEvents;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
};
const zSubsQuery = z.object({
  editing: z.array(z.string()).default([]),
  status: z.array(zGlobalAdminUI.shape.status).default([...licensePlans]),
  search: z.string().default('')
});
type StatusFilter = z.infer<typeof zSubsQuery>['status'];
const SubsTable = t0 => {
  const $ = _c(5);
  const {
    query,
    setQuery,
    subsQuery,
    profile
  } = t0;
  let t1;
  if ($[0] !== profile || $[1] !== query || $[2] !== setQuery || $[3] !== subsQuery) {
    t1 = profile && <div><div className="flex flex-col gap-2 w-[min(90vw,55em)]"><ActionCard classNames={{
          content: "flex-col",
          root: "self-start"
        }}><div className="flex items-center gap-2"><Chip.Group multiple={true} value={query.status} onChange={v => setQuery({
              status: v as StatusFilter
            })}>{globalAdminUiStatuses.map(_temp8)}</Chip.Group></div><div className="flex items-center gap-2 justify-between w-full"><TextInput placeholder="Search workspace names, owner names, or emails..." value={query.search} onChange={e => setQuery({
              search: e.currentTarget.value
            })} className="w-[25em]" rightSection={query.search ? <ActionIcon variant="subtle" onClick={() => setQuery({
              search: ""
            })}><HiX /></ActionIcon> : null} /><DownloadButton getContent={subsQuery.data ? () => {
              if (!subsQuery.data) {
                return;
              }
              const dataForCsv = subsQuery.data.map(_temp9);
              return objectToCsv(dataForCsv);
            } : undefined} filename={downloadFileName("subscriptions")} disabled={!subsQuery.data || isEmpty(subsQuery.data)}>Download CSV</DownloadButton></div></ActionCard>{match(subsQuery).with({
          isError: true
        }, _temp10).with({
          data: P.not(P.nullish)
        }, t2 => {
          const {
            data
          } = t2;
          const editingIds = query.editing;
          return !isEmpty(data) ? <div className="relative pt-4 flex flex-col gap-2">{data.map(d => <SubscriptionCard key={d.id} row={d} profile={profile} editing={editingIds.includes(d.id)} setEditing={() => setQuery({
              editing: editingIds.includes(d.id) ? editingIds.filter(e_0 => e_0 != d.id) : [...editingIds, d.id]
            })} />)}</div> : <LabelEmptyList>No subscriptions for the selected filters</LabelEmptyList>;
        }).with({
          isLoading: true
        }, _temp12).otherwise(_temp13)}</div></div>;
    $[0] = profile;
    $[1] = query;
    $[2] = setQuery;
    $[3] = subsQuery;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const SubscriptionCard = t0 => {
  const $ = _c(5);
  const {
    row,
    profile,
    editing,
    setEditing
  } = t0;
  let t1;
  if ($[0] !== editing || $[1] !== profile || $[2] !== row || $[3] !== setEditing) {
    t1 = <div className="flex items-center justify-between gap-4 p-2  rounded border">{editing ? <SubscriptionEditing row={row} profile={profile} setEditing={setEditing} /> : <><SubscriptionTitle license={row.cfg?.license} row={row} /><ActionIcon className="self-start" onClick={() => setEditing(true)}><HiPencil /></ActionIcon></>}</div>;
    $[0] = editing;
    $[1] = profile;
    $[2] = row;
    $[3] = setEditing;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const SubscriptionTitle = t0 => {
  const $ = _c(47);
  const {
    row: r
  } = t0;
  let t1;
  if ($[0] !== r.id) {
    t1 = nav.questions.getUrl({
      workspace: r.id
    });
    $[0] = r.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== r.workspace_cfg.name || $[3] !== t1.url) {
    t2 = <Link className="font-bold mr-auto" href={t1.url}>{r.workspace_cfg.name}</Link>;
    $[2] = r.workspace_cfg.name;
    $[3] = t1.url;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== r.status) {
    t3 = planColor(r.status);
    $[5] = r.status;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== r.status) {
    t4 = upperFirst(r.status);
    $[7] = r.status;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <Badge color={t3}>{t4}</Badge>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t2 || $[13] !== t5) {
    t6 = <div className="flex items-center gap-2">{t2}{t5}</div>;
    $[12] = t2;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="opacity-50">Created</span>;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== r.created_at) {
    t8 = fmtDate(r.created_at);
    $[16] = r.created_at;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <div>{t7}{" "}<span className="font-semibold whitespace-nowrap">{t8}</span></div>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== r.cfg || $[21] !== r.plan || $[22] !== r.status) {
    t10 = r.plan == "trial" && r.cfg?.license?.expires && <div><span className="opacity-50">{r.status == "expired" ? "Expired" : "Expires"}</span>{" "}<span data-status={r.status == "expired" ? "expired" : "expires"} className="font-semibold whitespace-nowrap data-[status=expired]:text-[var(--mantine-color-red-7)]">{fmtDate(r.cfg.license.expires)}</span></div>;
    $[20] = r.cfg;
    $[21] = r.plan;
    $[22] = r.status;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t9) {
    t11 = <div className="w-[14em] flex flex-col">{t9}{t10}</div>;
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span className="opacity-50">Owner</span>;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== r.owner_email || $[29] !== r.owner_name) {
    t13 = <div>{t12}{" "}<span className="font-semibold">{r.owner_name} ({r.owner_email})</span></div>;
    $[28] = r.owner_email;
    $[29] = r.owner_name;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== r.cfg) {
    t14 = r.cfg?.stripe?.subscriptionId && <StripeSubscriptionLink subscriptionId={r.cfg.stripe.subscriptionId} />;
    $[31] = r.cfg;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== r.id) {
    t15 = posthogUrl(r.id);
    $[33] = r.id;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t15.url) {
    t16 = <a href={t15.url} target="_blank">Posthog replays</a>;
    $[35] = t15.url;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t14 || $[38] !== t16) {
    t17 = <div className="flex flex-col items-end ml-auto">{t14}{t16}</div>;
    $[37] = t14;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t11 || $[41] !== t13 || $[42] !== t17) {
    t18 = <div className="flex gap-4 text-sm opacity-80">{t11}{t13}{t17}</div>;
    $[40] = t11;
    $[41] = t13;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t18 || $[45] !== t6) {
    t19 = <div className="grow gap-1 flex flex-col">{t6}{t18}</div>;
    $[44] = t18;
    $[45] = t6;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  return t19;
};
const SubscriptionEditing = t0 => {
  const $ = _c(38);
  const {
    row,
    profile,
    setEditing
  } = t0;
  let t1;
  if ($[0] !== row.id) {
    t1 = {
      id: row.id
    };
    $[0] = row.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== profile || $[3] !== t1) {
    t2 = {
      table: "workspace_global_admin",
      schema: WorkspaceTypes.zGlobalAdminRow,
      filter: t1,
      profile
    };
    $[2] = profile;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const sbSync = useQuerySbSync(t2);
  const dbRow = sbSync.data?.[0];
  const t3 = dbRow?.cfg ?? undefined;
  let t4;
  if ($[5] !== dbRow || $[6] !== profile || $[7] !== row.id || $[8] !== sbSync) {
    t4 = async t5 => {
      const {
        cfg,
        delta
      } = t5;
      if (!sbSync.isSuccess) {
        throw new Error("sbSync not ready");
      }
      if (dbRow) {
        await sbSync.updateCfg({
          id: row.id,
          cfg_updates: delta
        });
      } else {
        await sbSync.insert({
          id: row.id,
          cfg,
          created_by: profile.id,
          updated_by: profile.id
        });
      }
    };
    $[5] = dbRow;
    $[6] = profile;
    $[7] = row.id;
    $[8] = sbSync;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = {
      stored: t3,
      schema: WorkspaceTypes.zGlobalAdminRow,
      debounceSave: 2000,
      onUpdate: t4
    };
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const dbForm = useDbForm(t5);
  let t6;
  if ($[13] !== dbForm) {
    t6 = dbFormSubset(dbForm, "license");
    $[13] = dbForm;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const form = t6;
  let t7;
  if ($[15] !== form.values) {
    t7 = form.values ?? zLicense.parse({});
    $[15] = form.values;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const values = t7;
  let t8;
  if ($[17] !== row || $[18] !== values) {
    t8 = <SubscriptionTitle row={row} license={values} />;
    $[17] = row;
    $[18] = values;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== row.created_at) {
    t9 = D.fromISO(row.created_at);
    $[20] = row.created_at;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== form || $[23] !== t9) {
    t10 = <AdminLicenseForm className="pt-2" form={form} workspaceCreatedAt={t9} />;
    $[22] = form;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t8) {
    t11 = <div className="flex flex-col gap-2 grow">{t8}{t10}</div>;
    $[25] = t10;
    $[26] = t8;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== setEditing) {
    t12 = () => setEditing(false);
    $[28] = setEditing;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const t13 = dbForm.isDirty ? "dirty" : "clean";
  let t14;
  if ($[30] !== t13) {
    t14 = <HiCheckCircle data-status={t13} className="data-[status=dirty]:text-orange-500 data-[status=clean]:text-green-500" />;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t12 || $[33] !== t14) {
    t15 = <ActionIcon className="self-start" onClick={t12}>{t14}</ActionIcon>;
    $[32] = t12;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t11 || $[36] !== t15) {
    t16 = <div className="flex items-center justify-between gap-4 grow">{t11}{t15}</div>;
    $[35] = t11;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  return t16;
};
const nonStripePlans = ['enterprise', 'trial'] as const;
const AdminLicenseForm = t0 => {
  const $ = _c(33);
  const {
    className,
    form,
    workspaceCreatedAt
  } = t0;
  const {
    setFieldValue
  } = form;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== className || $[1] !== form || $[2] !== setFieldValue || $[3] !== workspaceCreatedAt) {
    const values = form.values ?? zLicense.parse({});
    const expiresDate = values.expires ? D.fromISO(values.expires) : undefined;
    const isExpired = expiresDate && expiresDate.diffNow().milliseconds < 0;
    const requiresExpiry = values.enabled && values.plan == "trial" || values.selfHost;
    const isNonStripePlan = nonStripePlans.includes(values.plan as any);
    if ($[10] !== className) {
      t6 = twMerge("flex flex-col gap-2", className);
      $[10] = className;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    t1 = "flex flex-col gap-3 grow";
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="flex items-center gap-2">Unrestricted<InfoTip label="When enabled, this workspace can be used and is not limited to a plan" /></div>;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    const t8 = !values.enabled;
    let t9;
    if ($[13] !== form || $[14] !== workspaceCreatedAt) {
      t9 = e => {
        const unrestricted = e.currentTarget.checked;
        const enabled = !unrestricted;
        if (!unrestricted) {
          form.setValues(trialLicenseCfg({
            workspaceCreatedAt
          }));
        } else {
          form.setFieldValue("enabled", enabled);
        }
      };
      $[13] = form;
      $[14] = workspaceCreatedAt;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    if ($[16] !== t8 || $[17] !== t9) {
      t2 = <Switch size="md" label={t7} checked={t8} onChange={t9} />;
      $[16] = t8;
      $[17] = t9;
      $[18] = t2;
    } else {
      t2 = $[18];
    }
    t3 = isNonStripePlan && values.enabled && <Select size="md" label={<div>Plan</div>} className="w-[10em]" data={nonStripePlans.map(_temp14)} value={values.plan} onChange={plan => {
      form.setValues(match(plan as (typeof nonStripePlans)[number]).with("enterprise", _temp15).with("trial", _ => trialLicenseCfg({
        workspaceCreatedAt
      })).exhaustive());
    }} />;
    t4 = values.enabled && values.plan == "enterprise" && <Switch size="md" label={<div className="flex items-center gap-2">Allow self-hosting<InfoTip label="When enabled, workspace admins can install a self-hosted version of the app" /></div>} checked={values.selfHost ?? false} onChange={e_0 => {
      setFieldValue("selfHost", e_0.currentTarget.checked);
    }} />;
    t5 = requiresExpiry && <><DateInput error={isExpired ? "License expired" : undefined} className="w-[15em]" label="Expiration Date" size="md" placeholder="Select expiration date" valueFormat="D MMM YYYY" value={expiresDate?.toJSDate() ?? null} onChange={date => {
        if (!date) {
          return;
        }
        setFieldValue("expires", date);
      }} getDayProps={_temp16} styles={{
        input: {
          color: expiresDate && expiresDate.diffNow().milliseconds < 0 ? "var(--mantine-color-red-5)" : undefined
        }
      }} required={requiresExpiry} clearable={!requiresExpiry} clearButtonProps={{
        onClick: () => {
          setFieldValue("expires", null);
        }
      }} /></>;
    $[0] = className;
    $[1] = form;
    $[2] = setFieldValue;
    $[3] = workspaceCreatedAt;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[19] !== form) {
    t7 = form.getInputProps("notes");
    $[19] = form;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t7) {
    t8 = <RichText className="w-full min-w-[30em]" placeholder="Notes about the license (visible to the workspace and global admins)" {...t7} />;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t1 || $[24] !== t2 || $[25] !== t3 || $[26] !== t4 || $[27] !== t5 || $[28] !== t8) {
    t9 = <div className={t1}>{t2}{t3}{t4}{t5}{t8}</div>;
    $[23] = t1;
    $[24] = t2;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t6 || $[31] !== t9) {
    t10 = <div className={t6}>{t9}</div>;
    $[30] = t6;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
};
const StripeSubscriptionLink = t0 => {
  const $ = _c(2);
  const {
    subscriptionId
  } = t0;
  const t1 = `https://dashboard.stripe.com/${env.NEXT_PUBLIC_STRIPE_KEY?.startsWith("pk_test") ? "test/" : ""}subscriptions/${subscriptionId}`;
  let t2;
  if ($[0] !== t1) {
    t2 = <a target="_blank" href={t1}>Stripe subscription</a>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
};
function _temp() {
  return "workspaces from stripe subscriptions";
}
function _temp2() {
  return "workspaces from recent stripe events";
}
function _temp3() {
  return "stripe subscriptions from app usage";
}
function _temp4(w) {
  return <Badge color="gray" variant="light" key={w.id}>{w.name}</Badge>;
}
function _temp5() {
  return <Alert color="green">Synced complete</Alert>;
}
function _temp6(m_0) {
  return <Alert color="red" title="Error syncing to monday">{m_0.error.message}</Alert>;
}
function _temp7() {
  return <></>;
}
function _temp8(status) {
  return <Chip key={status} value={status} color={planColor(status)} variant="filled">{upperFirst(status)}</Chip>;
}
function _temp9(r) {
  return {
    workspace_id: r.id,
    workspace_name: r.workspace_cfg.name,
    owner_name: r.owner_name,
    owner_email: r.owner_email,
    status: r.status,
    created_at: r.created_at,
    stripe_subscription_id: r.cfg?.stripe?.subscriptionId ?? undefined,
    expires: r.plan == "trial" ? r.cfg?.license?.expires ?? undefined : undefined
  };
}
function _temp10(t0) {
  const {
    error
  } = t0;
  return <Alert color="red" title="Error fetching subscriptions">{error.message}</Alert>;
}
function _temp11(i) {
  return <Skeleton key={i} h="2em" w="20em" />;
}
function _temp12() {
  return <div className="flex flex-col gap-2">{range(0, 4).map(_temp11)}</div>;
}
function _temp13() {
  return <></>;
}
function _temp14(p) {
  return {
    label: upperFirst(p),
    value: p
  };
}
function _temp15(plan_0) {
  return {
    plan: plan_0
  };
}
function _temp16(d) {
  return {
    weekend: false,
    highlightToday: D.fromISO(d).hasSame(D.now(), "day"),
    style: {
      color: D.fromISO(d).diffNow().milliseconds < 0 ? "var(--mantine-color-red-5)" : ""
    }
  };
}